import { Lead, Bank, Connector, User, Task, LeaderboardEntry, UserRole } from '@/types';

// User ID to Name mapping (static fallbacks for mock users)
export const userMap: Record<string, string> = {
  'u1': 'Anand Dubey',
  'u2': 'Rahul Upadhyay',
  'u3': 'Anurag Mishra',
  'u4': 'Shailesh Gupta',
  'u5': 'Yogesh',
};

// Dynamic cache populated at runtime from Firestore approvedUsers
const dynamicUserCache: Record<string, string> = {};

export const populateUserCache = (users: Array<{ id?: string; userId?: string; name: string }>) => {
  users.forEach(u => {
    // Map both userId and document ID to name for maximum compatibility
    if (u.userId && u.name) {
      dynamicUserCache[u.userId] = u.name;
    }
    if (u.id && u.name && u.id !== u.userId) {
      // Also map document ID if different from userId
      dynamicUserCache[u.id] = u.name;
    }
  });
};

export const getUserName = (userId: string): string => {
  if (!userId) return '';
  return dynamicUserCache[userId] || userMap[userId] || userId;
};

export const mockUser: User = {
  id: 'u1',
  name: 'Anand Dubey',
  email: 'anand@email.in',
  role: 'sales_agent', // Kept for backward compatibility
  roles: ['sales_agent'],
  primaryRole: 'sales_agent',
  avatar: '',
  monthlyTarget: 0,
  monthlyTargetCount: 0,
  loginTarget: 0,
  loginTargetCount: 0,
  currentDisbursement: 0,
  currentDisbursementCount: 0,
  currentLogin: 0,
  currentLoginCount: 0,
  commission: 0,
};

export const mockLeads: Lead[] = [
  {
    id: 'l1',
    category: 'loan' as const,
    firstName: 'Priya',
    lastName: 'Patel',
    mobile: '9876543210',
    email: 'priya@email.com',
    panCard: 'ABCDE1234F',
    loanType: 'home_loan',
    loanAmount: 4500000,
    loanTenure: 20,
    lender: 'HDFC Bank',
    customerCity: 'Mumbai',
    status: 'sanctioned',
    employmentType: 'salaried',
    monthlySalary: 85000,
    cibilScore: 780,
    assignedTo: 'u1', // Anand Dubey
    assignedBank: 'HDFC Bank',
    sanctionedAmount: 4200000,
    connectorId: 'c1',
    timeline: [],
    checklist: [],
    createdAt: new Date('2024-11-15'),
    updatedAt: new Date('2024-11-28'),
  },
  {
    id: 'l2',
    category: 'loan' as const,
    firstName: 'Amit',
    lastName: 'Kumar',
    mobile: '9988776655',
    loanType: 'home_loan',
    loanAmount: 3500000,
    loanTenure: 15,
    lender: 'SBI',
    customerCity: 'Delhi',
    status: 'logged_in',
    employmentType: 'salaried',
    monthlySalary: 65000,
    cibilScore: 720,
    assignedTo: 'u2', // Rahul Upadhyay
    assignedBank: 'SBI',
    connectorId: 'c5',
    timeline: [],
    checklist: [],
    createdAt: new Date('2024-11-20'),
    updatedAt: new Date('2024-11-27'),
  },
  {
    id: 'l3',
    category: 'loan' as const,
    firstName: 'Sunita',
    lastName: 'Reddy',
    mobile: '9112233445',
    loanType: 'lap',
    loanAmount: 8000000,
    loanTenure: 10,
    customerCity: 'Hyderabad',
    status: 'query',
    employmentType: 'self_employed_itr',
    monthlySalary: 150000,
    cibilScore: 690,
    assignedTo: 'u3', // Anurag Mishra
    timeline: [
      {
        id: 't1',
        type: 'note',
        content: 'Bank needs 3 years ITR instead of 2',
        createdAt: new Date('2024-11-26'),
        createdBy: 'Anurag Mishra'
      }
    ],
    checklist: [],
    createdAt: new Date('2024-11-22'),
    updatedAt: new Date('2024-11-26'),
  },
  {
    id: 'l4',
    category: 'loan' as const,
    firstName: 'Vikram',
    lastName: 'Singh',
    mobile: '9001122334',
    panCard: 'VWXYZ5678K',
    loanType: 'home_loan',
    loanAmount: 6000000,
    loanTenure: 25,
    customerCity: 'Bangalore',
    status: 'new_lead',
    employmentType: 'salaried',
    monthlySalary: 120000,
    assignedTo: 'u4', // Shailesh Gupta
    timeline: [],
    checklist: [],
    createdAt: new Date('2024-11-28'),
    updatedAt: new Date('2024-11-28'),
  },
  {
    id: 'l5',
    category: 'loan' as const,
    firstName: 'Meera',
    lastName: 'Joshi',
    mobile: '9556677889',
    loanType: 'home_loan',
    loanAmount: 3000000,
    loanTenure: 20,
    lender: 'ICICI Bank',
    customerCity: 'Pune',
    status: 'disbursed',
    employmentType: 'salaried',
    monthlySalary: 55000,
    cibilScore: 750,
    assignedTo: 'u1', // Anand Dubey
    assignedBank: 'ICICI Bank',
    sanctionedAmount: 2800000,
    disbursedAmount: 2800000,
    timeline: [],
    checklist: [],
    createdAt: new Date('2024-10-15'),
    updatedAt: new Date('2024-11-25'),
  },
  {
    id: 'l6',
    category: 'loan' as const,
    firstName: 'Rajesh',
    lastName: 'Gupta',
    mobile: '9887766554',
    loanType: 'business_loan',
    loanAmount: 2000000,
    loanTenure: 7,
    lender: 'Axis Bank',
    customerCity: 'Ahmedabad',
    status: 'verification_pending',
    employmentType: 'self_employed_itr',
    monthlySalary: 100000,
    cibilScore: 710,
    assignedTo: 'u5', // Yogesh
    assignedBank: 'Axis Bank',
    sanctionedAmount: 1800000,
    timeline: [],
    checklist: [],
    createdAt: new Date('2024-11-18'),
    updatedAt: new Date('2024-11-27'),
  },
];

export const mockBanks: Bank[] = [
  {
    id: 'b1',
    name: 'HDFC Bank',
    logo: '🏦',
    minCibil: 720,
    minSalary: 25000,
    minExperience: '3 years total, 1 year current',
    employmentTypes: ['salaried'],
    roiRange: { min: 8.5, max: 9.15 },
    processingFee: '₹3,000 + GST',
    maxTenure: 30,
    multiplier: '65x Net Salary',
    negativeProfiles: ['Lawyers', 'Police', 'Cash Salary'],
    negativeAreas: ['Slum', 'Red Light Area', 'Unapproved Colony'],
    ltvLimit: '90% up to 30L, 80% above 75L',
    avgTat: '4-5 Days',
    tags: ['Fast TAT', 'Strict CIBIL'],
    contactPerson: { name: 'Amit Singh', phone: '9876543210' },
  },
  {
    id: 'b2',
    name: 'SBI',
    logo: '🏛️',
    minCibil: 650,
    minSalary: 20000,
    minExperience: '2 years total',
    employmentTypes: ['salaried', 'self_employed_itr'],
    roiRange: { min: 8.4, max: 9.0 },
    processingFee: '0.35% of Loan Amount',
    maxTenure: 30,
    multiplier: '60x Net Salary',
    negativeProfiles: ['Cash Salary > 30%'],
    negativeAreas: ['Encroachment Area'],
    ltvLimit: '90% up to 30L, 75% above 75L',
    avgTat: '7-10 Days',
    tags: ['Low CIBIL OK', 'Govt Employees Preferred'],
  },
  {
    id: 'b3',
    name: 'ICICI Bank',
    logo: '🔶',
    minCibil: 700,
    minSalary: 30000,
    minExperience: '3 years total, 1 year current',
    employmentTypes: ['salaried', 'self_employed_pro'],
    roiRange: { min: 8.6, max: 9.25 },
    processingFee: '₹5,000 + GST or 0.5%',
    maxTenure: 30,
    multiplier: '70x Net Salary',
    negativeProfiles: ['Drivers', 'Security Guards'],
    negativeAreas: ['Negative Pin Codes List'],
    ltvLimit: '85% up to 50L, 75% above 75L',
    avgTat: '3-4 Days',
    tags: ['Fast TAT', 'High LTV'],
    contactPerson: { name: 'Priya Mehta', phone: '9988776655' },
  },
  {
    id: 'b4',
    name: 'Axis Bank',
    logo: '🔷',
    minCibil: 700,
    minSalary: 25000,
    minExperience: '2 years total, 6 months current',
    employmentTypes: ['salaried', 'self_employed_itr', 'self_employed_pro'],
    roiRange: { min: 8.7, max: 9.4 },
    processingFee: '1% of Loan Amount',
    maxTenure: 30,
    multiplier: '60x Net Salary',
    negativeProfiles: ['Daily Wage Workers'],
    negativeAreas: ['Industrial Zone'],
    ltvLimit: '90% up to 30L, 80% above 50L',
    avgTat: '5-6 Days',
    tags: ['All Employment Types', 'Flexible'],
  },
  {
    id: 'b5',
    name: 'Bajaj Finance',
    logo: '💰',
    minCibil: 680,
    minSalary: 35000,
    minExperience: '2 years total',
    employmentTypes: ['salaried', 'self_employed_itr'],
    roiRange: { min: 9.0, max: 11.0 },
    processingFee: '2% of Loan Amount',
    maxTenure: 25,
    multiplier: '55x Net Salary',
    negativeProfiles: ['Contract Employees'],
    negativeAreas: ['Rural Areas'],
    ltvLimit: '75% all cases',
    avgTat: '2-3 Days',
    tags: ['Fastest TAT', 'Premium Rates'],
  },
];

export const mockConnectors: Connector[] = [
  {
    id: 'c1',
    name: 'Rajesh Mehta',
    type: 'broker',
    whatsappNumber: '9876543210',
    secondaryNumber: '9876543211',
    address: '12, Andheri East, Near Metro Station',
    pincode: '400069',
    city: 'Mumbai',
    state: 'Maharashtra',
    email: 'rajesh.mehta@gmail.com',
    isFavorite: true,
    lastVisitDate: new Date('2024-11-28'),
    lastFollowUpDate: new Date('2024-11-28'),
    totalVisits: 15,
    createdAt: new Date('2024-06-01'),
    location: { lat: 19.1136, lng: 72.8697 },
    remark: 'Very active broker, brings quality leads regularly.',
  },
  {
    id: 'c2',
    name: 'Sundar Builders',
    type: 'builder',
    whatsappNumber: '9988776655',
    address: 'Tower B, Business Park, Whitefield',
    pincode: '560066',
    city: 'Bangalore',
    state: 'Karnataka',
    isFavorite: true,
    lastVisitDate: new Date('2024-11-25'),
    lastFollowUpDate: new Date('2024-11-26'),
    totalVisits: 8,
    createdAt: new Date('2024-07-15'),
    remark: 'Large builder with ongoing projects in Whitefield.',
  },
  {
    id: 'c3',
    name: 'Anil Gupta (CA)',
    type: 'ca',
    whatsappNumber: '9112233445',
    secondaryNumber: '9112233446',
    address: '45, Connaught Place, Block C',
    pincode: '110001',
    city: 'Delhi',
    state: 'Delhi',
    email: 'anil.gupta.ca@gmail.com',
    isFavorite: false,
    lastVisitDate: new Date('2024-11-20'),
    lastFollowUpDate: new Date('2024-11-22'),
    totalVisits: 5,
    createdAt: new Date('2024-08-10'),
    remark: 'Good for business loan referrals.',
  },
  {
    id: 'c4',
    name: 'Priya Financial Services',
    type: 'financial_advisor',
    whatsappNumber: '9001122334',
    address: 'Shop 23, Market Complex, Bandra',
    pincode: '400050',
    city: 'Mumbai',
    state: 'Maharashtra',
    isFavorite: false,
    totalVisits: 3,
    createdAt: new Date('2024-09-05'),
  },
  {
    id: 'c5',
    name: 'Sanjay Properties',
    type: 'channel_partner',
    whatsappNumber: '9556677889',
    secondaryNumber: '9556677890',
    address: '78, MG Road, Near City Mall',
    pincode: '411001',
    city: 'Pune',
    state: 'Maharashtra',
    email: 'sanjay.properties@yahoo.com',
    isFavorite: true,
    lastVisitDate: new Date('2024-11-27'),
    lastFollowUpDate: new Date('2024-11-27'),
    totalVisits: 12,
    createdAt: new Date('2024-05-20'),
    location: { lat: 18.5204, lng: 73.8567 },
    remark: 'Top performer in Pune region.',
  },
  {
    id: 'c6',
    name: 'Ramesh Connector',
    type: 'connector',
    whatsappNumber: '9887766554',
    address: 'Sector 21, Gandhinagar',
    pincode: '382021',
    city: 'Gandhinagar',
    state: 'Gujarat',
    isFavorite: false,
    totalVisits: 2,
    createdAt: new Date('2024-10-01'),
  },
];

// Banker Contacts Mock Data
export interface SMContact {
  id: string;
  name: string;
  bank: string;
  phone: string;
  email?: string;
  region: string;
  designation?: string;
  isFavorite: boolean;
  products: string[];
  reportReason?: string;
  isReported?: boolean;
  remark?: string;
  documents?: { id: string; name: string; type: string; size: number }[];
  createdBy: string;
  createdAt: Date;
}

export const mockSMContacts: SMContact[] = [
  { id: "sm1", name: "Amit Singh", bank: "HDFC Bank", phone: "9876543210", email: "amit.singh@hdfc.com", region: "Mumbai", isFavorite: true, products: ["home_loan", "lap"], createdBy: "Anand Dubey", createdAt: new Date('2024-10-01') },
  { id: "sm2", name: "Priya Mehta", bank: "ICICI Bank", phone: "9988776655", email: "priya.mehta@icici.com", region: "Delhi", isFavorite: true, products: ["home_loan", "personal_loan"], createdBy: "Anand Dubey", createdAt: new Date('2024-10-05') },
  { id: "sm3", name: "Rahul Verma", bank: "SBI", phone: "9112233445", region: "Bangalore", isFavorite: false, products: ["home_loan", "business_loan"], createdBy: "Rahul Upadhyay", createdAt: new Date('2024-10-10') },
  { id: "sm4", name: "Sneha Gupta", bank: "Axis Bank", phone: "9001122334", email: "sneha.g@axis.com", region: "Pune", isFavorite: false, products: ["car_loan", "personal_loan"], createdBy: "Anand Dubey", createdAt: new Date('2024-10-15') },
  { id: "sm5", name: "Vikram Sharma", bank: "Kotak Bank", phone: "9223344556", email: "vikram.s@kotak.com", region: "Chennai", isFavorite: false, products: ["home_loan", "lap", "insurance"], createdBy: "Anurag Mishra", createdAt: new Date('2024-10-20') },
];

// Product options for SM mapping
export const SM_PRODUCT_OPTIONS = [
  { value: 'home_loan', label: 'Home Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'business_loan', label: 'Business Loan' },
  { value: 'car_loan', label: 'Car Loan' },
  { value: 'lap', label: 'Loan Against Property' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'others', label: 'Others' },
];

// Broadcast Groups
export interface BroadcastGroup {
  id: string;
  name: string;
  contactIds: { id: string; type: 'customer' | 'partner' | 'sm' | 'team' }[];
  createdBy: string;
  createdAt: Date;
}

export const mockBroadcastGroups: BroadcastGroup[] = [
  {
    id: 'bg1',
    name: 'Top Partners',
    contactIds: [
      { id: 'c1', type: 'partner' },
      { id: 'c2', type: 'partner' },
      { id: 'c5', type: 'partner' },
    ],
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-01'),
  },
  {
    id: 'bg2',
    name: 'All SMs',
    contactIds: [
      { id: 'sm1', type: 'sm' },
      { id: 'sm2', type: 'sm' },
      { id: 'sm3', type: 'sm' },
    ],
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-05'),
  },
];

// Broadcast Templates
export interface BroadcastTemplate {
  id: string;
  name: string;
  message: string;
  createdBy: string;
  createdAt: Date;
}

export const mockBroadcastTemplates: BroadcastTemplate[] = [
  {
    id: 'bt1',
    name: 'Diwali Wishes',
    message: '🎉 Wishing you a very Happy Diwali! May this festival bring prosperity and happiness to you and your family.',
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-01'),
  },
  {
    id: 'bt2',
    name: 'New Offer Announcement',
    message: '📢 Exciting News! We have a special offer for you. Get the lowest interest rates on Home Loans starting at 8.5%. Contact us today!',
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-10'),
  },
  {
    id: 'bt3',
    name: 'File Details',
    message: 'Hi, sharing the file details for your reference. Please check and let me know if you need any clarification.',
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-15'),
  },
];

export const mockTasks: Task[] = [
  {
    id: 't1',
    title: 'Follow up with Sunita Reddy',
    description: 'Get additional ITR documents',
    type: 'reminder',
    leadId: 'l3',
    dueDate: new Date('2024-11-29T14:00:00'),
    priority: 'medium',
    completed: false,
    attachments: [],
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-28T09:00:00'),
  },
  {
    id: 't2',
    title: 'Collect OTC documents from Priya Patel',
    description: 'Original property papers ready for collection',
    type: 'reminder',
    leadId: 'l1',
    dueDate: new Date('2024-11-29T16:30:00'),
    priority: 'medium',
    completed: false,
    attachments: [],
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-28T10:00:00'),
  },
  {
    id: 't3',
    title: 'Call Vikram Singh for property details',
    description: 'New lead - initial property verification needed',
    type: 'reminder',
    leadId: 'l4',
    dueDate: new Date('2024-11-30T11:00:00'),
    priority: 'medium',
    completed: false,
    attachments: [],
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-29T08:00:00'),
  },
  {
    id: 't4',
    title: 'Bank statement follow-up',
    description: 'Remind Amit Kumar about pending documents',
    type: 'reminder',
    leadId: 'l2',
    dueDate: new Date('2024-11-30T09:00:00'),
    priority: 'medium',
    completed: false,
    attachments: [],
    createdBy: 'Anand Dubey',
    createdAt: new Date('2024-11-29T09:00:00'),
  },
];

export const loginLeaderboard: LeaderboardEntry[] = [
  { rank: 1, userId: 'u1', name: 'Anand Dubey', amount: 5500000, target: 8000000, count: 12, targetCount: 15 },
  { rank: 2, userId: 'u2', name: 'Rahul Upadhyay', amount: 4800000, target: 7000000, count: 10, targetCount: 12 },
  { rank: 3, userId: 'u3', name: 'Direct', amount: 4200000, target: 6500000, count: 9, targetCount: 11 },
  { rank: 4, userId: 'u4', name: 'Shailesh Gupta', amount: 3800000, target: 6000000, count: 8, targetCount: 10 },
  { rank: 5, userId: 'u5', name: 'Yogesh Mishra', amount: 3500000, target: 5500000, count: 7, targetCount: 9 },
  { rank: 6, userId: 'u6', name: 'Pragati Patil', amount: 3200000, target: 5000000, count: 6, targetCount: 8 },
  { rank: 7, userId: 'u7', name: 'Bittu Singh Rajput', amount: 2900000, target: 4500000, count: 6, targetCount: 7 },
  { rank: 8, userId: 'u8', name: 'Vikas Pandey', amount: 2600000, target: 4000000, count: 5, targetCount: 7 },
  { rank: 9, userId: 'u9', name: 'Ashish', amount: 2300000, target: 3500000, count: 5, targetCount: 6 },
];

export const disbursedLeaderboard: LeaderboardEntry[] = [
  { rank: 1, userId: 'u1', name: 'Anand Dubey', amount: 3200000, target: 5000000, count: 8, targetCount: 10 },
  { rank: 2, userId: 'u2', name: 'Rahul Upadhyay', amount: 2800000, target: 4500000, count: 7, targetCount: 9 },
  { rank: 3, userId: 'u3', name: 'Direct', amount: 2500000, target: 4000000, count: 6, targetCount: 8 },
  { rank: 4, userId: 'u4', name: 'Shailesh Gupta', amount: 2200000, target: 3500000, count: 5, targetCount: 7 },
  { rank: 5, userId: 'u5', name: 'Yogesh Mishra', amount: 1900000, target: 3000000, count: 5, targetCount: 6 },
  { rank: 6, userId: 'u6', name: 'Pragati Patil', amount: 1700000, target: 2800000, count: 4, targetCount: 5 },
  { rank: 7, userId: 'u7', name: 'Bittu Singh Rajput', amount: 1500000, target: 2500000, count: 4, targetCount: 5 },
  { rank: 8, userId: 'u8', name: 'Vikas Pandey', amount: 1300000, target: 2200000, count: 3, targetCount: 4 },
  { rank: 9, userId: 'u9', name: 'Ashish', amount: 1100000, target: 2000000, count: 3, targetCount: 4 },
];

export const leaderboardData = disbursedLeaderboard.map(e => ({
  rank: e.rank,
  name: e.name,
  disbursement: e.amount,
  deals: e.count,
}));

// Team Members for Broadcast
export interface TeamMember {
  id: string;
  name: string;
  phone: string;
  roles?: UserRole[]; // New role structure
  hasAppAccess: boolean;
  showInLeaderboard: boolean;
}

export const mockTeamMembers: TeamMember[] = [
  { id: 'tm1', name: 'Anand Dubey', phone: '9082910901', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm2', name: 'Rahul Upadhyay', phone: '8976101407', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm3', name: 'Direct', phone: '9415094980', roles: ['admin', 'sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm4', name: 'Pragati Patil', phone: '8104467764', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm5', name: 'Bittu Singh Rajput', phone: '8005832077', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm6', name: 'Harsshal', phone: '9987359499', roles: ['member'], hasAppAccess: false, showInLeaderboard: false },
  { id: 'tm7', name: 'Vikas Pandey', phone: '9004337511', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm8', name: 'Shailesh Gupta', phone: '8850036619', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm9', name: 'Yogesh Mishra', phone: '8924083636', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm10', name: 'Ruchi Pandey', phone: '9026774942', roles: ['app_user'], hasAppAccess: true, showInLeaderboard: false },
  { id: 'tm11', name: 'Ashish', phone: '8169603163', roles: ['sales_agent'], hasAppAccess: true, showInLeaderboard: true },
  { id: 'tm12', name: 'Anjana Raida', phone: '9022252622', roles: ['member'], hasAppAccess: false, showInLeaderboard: false },
  { id: 'tm13', name: 'Khusbhu', phone: '8433692029', roles: ['app_user'], hasAppAccess: true, showInLeaderboard: false },
  { id: 'tm14', name: 'Vivek Mishra', phone: '8802024312', roles: ['app_user'], hasAppAccess: true, showInLeaderboard: false },
];
